<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';

import Notification from '@/components/base/Notification.vue';
import GiftOptions from '@/components/checkout/steps/delivery/GiftOptions.vue';
import HeatResistantPackagingCheckbox from '@/components/checkout/steps/delivery/HeatResistantPackagingCheckbox.vue';
import RequestedDeliveryShippingOffer from '@/components/checkout/steps/delivery/RequestedDeliveryShippingOffer.vue';
import ShippingOfferList from '@/components/checkout/steps/delivery/ShippingOfferList.vue';
import {
  ExtendedShippingOfferSet,
  GiftOptions as GiftOptionsModel,
} from '@/composables/useDelivery';
import { DateString } from '@/utils/dateTime';

defineProps<{
  addressKey: string;
  containsGift?: boolean;
  offerPackingSlipMessage?: boolean;
  selectedDeliveryDate?: DateString;
  shippingOffers: ExtendedShippingOfferSet['shippingOffers'];
}>();

const addHeatResistantPackaging = defineModel<boolean>('addHeatResistantPackaging', {
  required: true,
});
const giftOptions = defineModel<GiftOptionsModel>('giftOptions', {
  required: true,
});
const selectedShippingOffer = defineModel<ExtendedShippingOfferSet['selectedShippingOffer']>(
  'selectedShippingOffer',
  {
    required: true,
  },
);

const emit = defineEmits<{
  'selected-delivery-option': [boolean];
}>();

const selectedOfferHeatSplit = computed(
  (): boolean =>
    selectedShippingOffer.value?.carrier === 'None' &&
    selectedShippingOffer.value?.carrierCode === 'HS',
);

const recommendHeatResistantPackaging = computed(
  (): boolean =>
    !!selectedShippingOffer.value?.containsMeltables &&
    !selectedShippingOffer.value.heatResistantIncluded,
);

const selectedOfferIncludesHeatResistantPackaging = computed(
  (): boolean => selectedShippingOffer.value?.heatResistantIncluded ?? false,
);

const showHeatResistantInfo = computed(
  (): boolean =>
    recommendHeatResistantPackaging.value || selectedOfferIncludesHeatResistantPackaging.value,
);

const deliveryWarnings = computed(() => selectedShippingOffer.value?.warnings ?? []);

watch(selectedShippingOffer, () => {
  emit('selected-delivery-option', true);
});

onMounted(() => {
  emit('selected-delivery-option', false);
});

const giftIcon = nutshell['img/gift-box.svg'];
</script>

<template>
  <div>
    <template v-if="deliveryWarnings.length">
      <Notification
        v-for="deliveryWarning in deliveryWarnings"
        :key="deliveryWarning"
        class="mt-3 md:mt-6"
      >
        <div v-html="deliveryWarning" />
      </Notification>
    </template>
    <Notification v-if="showHeatResistantInfo" class="mt-3 bg-amber-200 md:mt-6">
      <div v-if="recommendHeatResistantPackaging">
        <p class="mb-0 font-semibold">
          It’s hot 🔥 outside! And it looks like you’ve added an item that may melt during transit.
        </p>
        <p class="mt-1 mb-0">
          We recommend you choose heat-resistant packaging to keep that from happening, since we
          can’t guarantee it won’t melt.
        </p>
      </div>
      <div v-else>
        <p class="mb-0 font-semibold">
          <template v-if="selectedOfferHeatSplit">
            It’s hot 🔥 outside, so we’ll ship some of your items in heat-resistant packaging to
            keep them from melting. The rest of your order will arrive separately.
          </template>
          <template v-else>
            It’s hot 🔥 outside, so we’ve added complimentary heat-resistant packaging to your order
            to keep your goodies from melting.
          </template>
        </p>
      </div>
    </Notification>
    <RequestedDeliveryShippingOffer
      v-if="selectedDeliveryDate && shippingOffers.length"
      :shippingOffer="shippingOffers[0]"
    />
    <template v-else>
      <ShippingOfferList
        v-if="shippingOffers.length"
        v-model="selectedShippingOffer"
        class="mt-3"
        :addressKey
        :shippingOffers
      />
    </template>
    <HeatResistantPackagingCheckbox
      v-if="showHeatResistantInfo"
      v-model="addHeatResistantPackaging"
      class="mt-3 md:mt-6"
      :addressKey
      :heatResistantPackagingIncluded="selectedOfferIncludesHeatResistantPackaging"
    />
    <slot />
    <template v-if="offerPackingSlipMessage">
      <div
        class="flex items-start mt-6 text-base font-bold"
        data-test="shipment-delivery-gift-options-title"
      >
        Gift Message
        <img :src="giftIcon" alt="Gift" class="w-5 ml-2" />
      </div>
      <GiftOptions
        v-model:includeGiftMessage="giftOptions.includeGiftMessage"
        v-model:message="giftOptions.message"
        v-model:sender="giftOptions.sender"
        class="mt-4"
        data-test="shipment-delivery-gift-options"
        :containsGift
      />
    </template>
  </div>
</template>
